// 控制会话的统一状态模型。
//
// 信令、WebRTC 和数据通道各自有一套状态，直接把它们的原始文本显示给
// 用户会造成“已配对但没有画面”“连接已断开却仍在重连”等歧义。这里定义
// 一套跨桌面 Client/Web 控制端共用的、与底层实现无关的会话状态。

export type ConnectionPhase =
  | 'idle'
  | 'connecting'
  | 'awaiting-approval'
  | 'paired'
  | 'negotiating'
  | 'connected'
  | 'recovering'
  | 'stalled'
  | 'failed'
  | 'closed';

export type ConnectionReason =
  | 'manual'
  | 'signaling'
  | 'approval'
  | 'pairing'
  | 'negotiating'
  | 'network'
  | 'timeout'
  | 'permission'
  | 'busy'
  | 'offline'
  | 'authentication'
  | 'self-device'
  | 'remote-ended'
  | 'rejected'
  | 'version'
  | 'unknown';

export interface ConnectionState {
  phase: ConnectionPhase;
  reason: ConnectionReason;
  /** 已经过安全处理的用户可读提示，不应包含 token、地址或原始协议数据。 */
  message: string;
  retryable: boolean;
  reconnectAttempt: number;
  lastActivityAt: number | null;
  startedAt: number | null;
  connectedAt: number | null;
  updatedAt: number;
}

export interface ConnectionStateEvent {
  phase: ConnectionPhase;
  reason: ConnectionReason;
  retryable: boolean;
  at: number;
}

export const CONNECTION_HISTORY_LIMIT = 32;

/**
 * 只保留有限的状态轨迹，供脱敏诊断使用。轨迹不携带提示文本、设备编号、
 * 地址或任何会话内容，避免为了排障把隐私数据带出本机。
 */
export function appendConnectionHistory(
  history: readonly ConnectionStateEvent[],
  state: Pick<ConnectionState, 'phase' | 'reason' | 'retryable'>,
  at = Date.now(),
): ConnectionStateEvent[] {
  const next = [...history, { phase: state.phase, reason: state.reason, retryable: state.retryable, at }];
  return next.length > CONNECTION_HISTORY_LIMIT ? next.slice(-CONNECTION_HISTORY_LIMIT) : next;
}

export interface ConnectionDiagnostics extends ConnectionState {
  /** 当前会话内自动恢复的总次数，包含已经成功的恢复。 */
  reconnectCount: number;
  signalingState: 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'unknown';
  peerConnectionState: string;
  iceConnectionState: string;
  controlReady: boolean;
  streamReady: boolean;
  remoteInputReady: boolean | null;
  peerDeviceId: string | null;
  lastActivityAgeMs: number | null;
  /** 最近一次收到控制通道消息或心跳回执的时间间隔。 */
  lastControlActivityAgeMs: number | null;
  /** 最近一次收到远端视频活动的时间间隔。 */
  lastVideoActivityAgeMs: number | null;
  lastControlActivityAt: number | null;
  lastVideoActivityAt: number | null;
  /** 从本次连接开始到第一条远端视频轨道到达的时间。 */
  firstFrameMs: number | null;
  /** 最近一次视频统计中的实际画面尺寸；不包含地址或会话内容。 */
  videoWidth: number | null;
  videoHeight: number | null;
  /** 最近一次视频统计中的编码和实现信息，仅用于脱敏诊断。 */
  videoCodec: string | null;
  videoImplementation: string | null;
  /** 最近一次关键鼠标/键盘操作在被控端原生执行完成的往返时间。 */
  inputRttMs: number | null;
  /** 最近一段连接状态轨迹；只包含阶段、分类和时间，不包含原始错误文本。 */
  stateHistory: ConnectionStateEvent[];
}

export interface ConnectionFailure {
  message: string;
  reason: Exclude<ConnectionReason, 'manual' | 'signaling' | 'approval' | 'pairing' | 'negotiating' | 'network'>;
  retryable: boolean;
}

export function createInitialConnectionState(now = Date.now()): ConnectionState {
  return {
    phase: 'idle',
    reason: 'manual',
    message: '',
    retryable: false,
    reconnectAttempt: 0,
    lastActivityAt: null,
    startedAt: null,
    connectedAt: null,
    updatedAt: now,
  };
}

function safeFailureText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 256);
}

/**
 * 将服务端/被控端可能返回的内部原因转换成稳定的用户提示。
 * 未知英文不会直接展示，避免把协议或实现细节泄露给普通用户。
 */
export function classifyConnectionFailure(value: unknown): ConnectionFailure {
  const raw = safeFailureText(value);
  const lower = raw.toLowerCase();

  if (lower.includes('invalid account token') || /登录已过期|账号.*失效|令牌/.test(raw)) {
    return { message: '登录状态已过期，请重新登录后再连接。', reason: 'authentication', retryable: false };
  }
  if (lower.includes('device belongs to another account') || /其他账号|不属于当前账号/.test(raw)) {
    return { message: '这台设备不属于当前账号，请切换账号或重新绑定。', reason: 'authentication', retryable: false };
  }
  if (/这是当前设备|当前设备|无需远程控制/.test(raw) || lower.includes('current device')) {
    return { message: '这是当前设备，不能连接控制自己。', reason: 'self-device', retryable: false };
  }
  if (/版本过旧|请升级|version/.test(raw)) {
    return { message: '对方客户端版本较旧，请更新后再连接。', reason: 'version', retryable: false };
  }
  if (/密码不正确|密码错误|password/.test(raw)) {
    return { message: '连接密码不正确，请检查后重试。', reason: 'rejected', retryable: false };
  }
  if (/未允许|未及时回应|连接已取消|重新发起|rejected|cancel/.test(raw)) {
    return { message: raw && /[\u4e00-\u9fff]/.test(raw) ? raw : '对方没有允许本次连接。', reason: 'rejected', retryable: false };
  }
  if (/使用中|正在连接|busy|in use/.test(raw)) {
    return { message: '对方正在处理另一个连接，请稍后重试。', reason: 'busy', retryable: true };
  }
  if (/频繁|rate.?limit|too many/.test(raw)) {
    return { message: '请求过于频繁，请稍后再试。', reason: 'busy', retryable: false };
  }
  if (/屏幕|画面|录制|辅助功能|权限|screen|permission/.test(raw)) {
    return { message: '对方暂时无法共享画面，请检查屏幕录制权限后重试。', reason: 'permission', retryable: true };
  }
  if (lower.includes('target device offline') || /设备当前不可用|目标设备.*离线|不在线|offline/.test(raw)) {
    return { message: '目标设备当前不在线，请确认 LinkDesk 已打开。', reason: 'offline', retryable: true };
  }
  if (/超时|无响应|timeout|no response/.test(raw)) {
    return { message: '连接响应超时，请检查网络后重试。', reason: 'timeout', retryable: true };
  }
  if (raw && /[\u4e00-\u9fff]/.test(raw)) {
    return { message: raw, reason: 'unknown', retryable: false };
  }
  return { message: '暂时无法连接，请检查网络后重试。', reason: 'unknown', retryable: true };
}
